/**
 * Asset optimization utilities for performance enhancement
 */

// Image optimization and lazy loading
export class ImageOptimizer {
  private static loadedImages = new Set<string>()
  private static imageCache = new Map<string, HTMLImageElement>()
  
  // Preload critical images
  static preloadCriticalImages(urls: string[]): Promise<void[]> {
    return Promise.all(
      urls.map(url => this.preloadImage(url))
    )
  }
  
  // Preload single image
  static preloadImage(url: string): Promise<void> {
    if (this.loadedImages.has(url)) {
      return Promise.resolve()
    }
    
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        this.loadedImages.add(url)
        this.imageCache.set(url, img)
        resolve()
      }
      img.onerror = reject
      img.src = url
    })
  }
  
  // Lazy load images with intersection observer
  static setupLazyLoading(): void {
    if (!('IntersectionObserver' in window)) {
      return // Fallback for older browsers
    }
    
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement
          const src = img.dataset.src
          if (src) {
            img.src = src
            img.classList.remove('lazy')
            imageObserver.unobserve(img)
          }
        }
      })
    }, {
      rootMargin: '50px 0px',
      threshold: 0.01
    })
    
    // Observe all lazy images
    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img)
    })
  }
  
  // Get optimized image URL (for future CDN integration)
  static getOptimizedImageUrl(url: string, options?: {
    width?: number
    height?: number
    quality?: number
    format?: 'webp' | 'avif' | 'jpg' | 'png'
  }): string {
    // For now, return original URL
    // In production, this would integrate with a CDN like Cloudinary or ImageKit
    return url
  }
}

// Font optimization
export class FontOptimizer {
  private static loadedFonts = new Set<string>()
  
  // Preload critical fonts
  static preloadFonts(fonts: Array<{ family: string; weight?: string; style?: string }>): void {
    fonts.forEach(font => {
      const fontKey = `${font.family}-${font.weight || '400'}-${font.style || 'normal'}`
      
      if (this.loadedFonts.has(fontKey)) {
        return
      }
      
      // Create font preload link
      const link = document.createElement('link')
      link.rel = 'preload'
      link.as = 'font'
      link.type = 'font/woff2'
      link.crossOrigin = 'anonymous'
      
      // Set href for the font file
      const fontWeight = font.weight || '400'
      const weightName = fontWeight === '400' ? 'Regular' : 
                        fontWeight === '500' ? 'Medium' :
                        fontWeight === '600' ? 'SemiBold' :
                        fontWeight === '700' ? 'Bold' : 'Regular'
      
      link.href = `/demo-assets/fonts/${font.family}-${weightName}.woff2`
      
      // Only append if the font file might exist
      if (font.family === 'Inter') {
        document.head.appendChild(link)
      }
      this.loadedFonts.add(fontKey)
    })
  }
  
  // Load fonts with font display swap
  static loadFontWithSwap(fontFamily: string): void {
    if ('fonts' in document) {
      const font = new FontFace(fontFamily, `url(/fonts/${fontFamily}.woff2)`, {
        display: 'swap'
      })
      
      font.load().then(() => {
        document.fonts.add(font)
      }).catch(console.error)
    }
  }
}

// CSS optimization
export class CSSOptimizer {
  // Load critical CSS inline, defer non-critical
  static loadCriticalCSS(criticalCSS: string): void {
    const style = document.createElement('style')
    style.textContent = criticalCSS
    document.head.appendChild(style)
  }
  
  // Load non-critical CSS asynchronously
  static loadNonCriticalCSS(href: string): void {
    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'style'
    link.href = href
    link.onload = () => {
      link.rel = 'stylesheet'
    }
    document.head.appendChild(link)
  }
  
  // Remove unused CSS (for build process)
  static removeUnusedCSS(): void {
    // This would integrate with PurgeCSS or similar in build process
    console.log('CSS optimization would run during build')
  }
}

// JavaScript optimization
export class JSOptimizer {
  private static loadedModules = new Set<string>()
  
  // Preload critical modules
  static preloadModule(moduleUrl: string): void {
    if (this.loadedModules.has(moduleUrl)) {
      return
    }
    
    const link = document.createElement('link')
    link.rel = 'modulepreload'
    link.href = moduleUrl
    document.head.appendChild(link)
    this.loadedModules.add(moduleUrl)
  }
  
  // Load module with retry logic
  static async loadModuleWithRetry(
    moduleFactory: () => Promise<any>, 
    maxRetries = 3
  ): Promise<any> {
    let lastError: Error
    
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await moduleFactory()
      } catch (error) {
        lastError = error as Error
        console.warn(`Module load attempt ${i + 1} failed:`, error)
        
        // Wait before retry
        if (i < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)))
        }
      }
    }
    
    throw lastError!
  }
}

// Resource hints for better loading
export class ResourceHints {
  // Add DNS prefetch for external domains
  static addDNSPrefetch(domains: string[]): void {
    domains.forEach(domain => {
      const link = document.createElement('link')
      link.rel = 'dns-prefetch'
      link.href = `//${domain}`
      document.head.appendChild(link)
    })
  }
  
  // Add preconnect for critical external resources
  static addPreconnect(urls: string[]): void {
    urls.forEach(url => {
      const link = document.createElement('link')
      link.rel = 'preconnect'
      link.href = url
      link.crossOrigin = 'anonymous'
      document.head.appendChild(link)
    })
  }
  
  // Add prefetch for likely next pages
  static addPrefetch(urls: string[]): void {
    urls.forEach(url => {
      const link = document.createElement('link')
      link.rel = 'prefetch'
      link.href = url
      document.head.appendChild(link)
    })
  }
}

// Performance monitoring
export class PerformanceMonitor {
  private static metrics: Record<string, number> = {}
  
  // Mark performance timing
  static mark(name: string): void {
    if ('performance' in window && performance.mark) {
      performance.mark(name)
    }
    this.metrics[name] = Date.now()
  }
  
  // Measure performance between marks
  static measure(name: string, startMark: string, endMark?: string): number {
    if ('performance' in window && performance.measure) {
      performance.measure(name, startMark, endMark)
      
      const entries = performance.getEntriesByName(name, 'measure')
      if (entries.length > 0) {
        return entries[entries.length - 1].duration
      }
    }
    
    // Fallback calculation
    const startTime = this.metrics[startMark]
    const endTime = endMark ? this.metrics[endMark] : Date.now()
    return endTime - startTime
  }
  
  // Get Core Web Vitals
  static getCoreWebVitals(): Promise<{
    lcp?: number
    fid?: number
    cls?: number
    fcp?: number
    ttfb?: number
  }> {
    return new Promise((resolve) => {
      const vitals: any = {}
      
      // Largest Contentful Paint
      if ('PerformanceObserver' in window) {
        try {
          const lcpObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries()
            const lastEntry = entries[entries.length - 1]
            vitals.lcp = lastEntry.startTime
          })
          lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] })
          
          // First Contentful Paint
          const fcpObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries()
            if (entries.length > 0) {
              vitals.fcp = entries[0].startTime
            }
          })
          fcpObserver.observe({ entryTypes: ['paint'] })
          
          // Cumulative Layout Shift
          const clsObserver = new PerformanceObserver((list) => {
            let clsValue = 0
            for (const entry of list.getEntries()) {
              if (!(entry as any).hadRecentInput) {
                clsValue += (entry as any).value
              }
            }
            vitals.cls = clsValue
          })
          clsObserver.observe({ entryTypes: ['layout-shift'] })
          
        } catch (error) {
          console.warn('Performance observer not supported:', error)
        }
      }
      
      // Time to First Byte
      if (performance.timing) {
        vitals.ttfb = performance.timing.responseStart - performance.timing.navigationStart
      }
      
      // Resolve after a delay to collect metrics
      setTimeout(() => resolve(vitals), 3000)
    })
  }
  
  // Log performance metrics
  static logMetrics(): void {
    this.getCoreWebVitals().then(vitals => {
      console.log('🚀 Performance Metrics:', {
        ...vitals,
        customMarks: this.metrics
      })
    })
  }
}